/**
 * Audio management utilities for the Spinner Wheel
 */

// Minimum interval between tick sounds (ms)
const TICK_THROTTLE_MS = 45;

type AudioContextConstructor = new () => AudioContext;
type WindowWithWebAudio = Window &
  typeof globalThis & {
    webkitAudioContext?: AudioContextConstructor;
  };

function getAudioContextConstructor(): AudioContextConstructor | null {
  if (typeof window === 'undefined') return null;

  const win = window as WindowWithWebAudio;
  return win.AudioContext || win.webkitAudioContext || null;
}

/**
 * Create an audio manager instance
 */
export function createAudioManager(tickPath: string, celebrationPath: string) {
  let tickAudio: HTMLAudioElement | null = null;
  let celebrationAudio: HTMLAudioElement | null = null;
  let audioContext: AudioContext | null = null;
  let tickBuffer: AudioBuffer | null = null;
  let tickBufferPromise: Promise<AudioBuffer | null> | null = null;
  let celebrationBuffer: AudioBuffer | null = null;
  let celebrationBufferPromise: Promise<AudioBuffer | null> | null = null;
  let lastPlay = 0;
  let tickVolumeValue = 0.5;
  let celebrationVolumeValue = 0.7;

  const getAudioContext = () => {
    if (typeof window === 'undefined') return;

    if (!audioContext) {
      const AudioContextCtor = getAudioContextConstructor();
      if (!AudioContextCtor) return null;

      audioContext = new AudioContextCtor();
    }

    return audioContext;
  };

  const loadBuffer = (
    path: string,
    getBuffer: () => AudioBuffer | null,
    setBuffer: (buffer: AudioBuffer) => void,
    getPromise: () => Promise<AudioBuffer | null> | null,
    setPromise: (promise: Promise<AudioBuffer | null> | null) => void
  ) => {
    const buffer = getBuffer();
    if (buffer) return Promise.resolve(buffer);

    const existingPromise = getPromise();
    if (existingPromise) return existingPromise;

    const context = getAudioContext();
    if (!context) return null;

    const promise = fetch(path)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Unable to load audio: ${response.status}`);
        }
        return response.arrayBuffer();
      })
      .then((audioData) => context.decodeAudioData(audioData))
      .then((buffer) => {
        setBuffer(buffer);
        return buffer;
      })
      .catch(() => {
        setPromise(null);
        return null;
      });

    setPromise(promise);
    return promise;
  };

  const loadTickBuffer = () => {
    return loadBuffer(
      tickPath,
      () => tickBuffer,
      (buffer) => {
        tickBuffer = buffer;
      },
      () => tickBufferPromise,
      (promise) => {
        tickBufferPromise = promise;
      }
    );
  };

  const loadCelebrationBuffer = () => {
    return loadBuffer(
      celebrationPath,
      () => celebrationBuffer,
      (buffer) => {
        celebrationBuffer = buffer;
      },
      () => celebrationBufferPromise,
      (promise) => {
        celebrationBufferPromise = promise;
      }
    );
  };

  const resumeAudioContext = (context: AudioContext) => {
    if (context.state === 'suspended') {
      void context.resume().catch(() => {
        // Ignore autoplay/resume errors.
      });
    }
  };

  const playBuffer = (buffer: AudioBuffer, volume: number) => {
    const context = getAudioContext();
    if (!context) return false;

    resumeAudioContext(context);

    const source = context.createBufferSource();
    const gain = context.createGain();
    source.buffer = buffer;
    gain.gain.value = volume;
    source.connect(gain);
    gain.connect(context.destination);
    source.start();

    return true;
  };

  const playWebAudioTick = () => {
    const context = getAudioContext();
    if (!context) return false;

    resumeAudioContext(context);

    if (!tickBuffer) {
      void loadTickBuffer();
      void loadCelebrationBuffer();
      // Safari can block for tens of milliseconds in HTMLAudioElement.play().
      // Skipping early ticks is preferable to janking the spin while the buffer
      // is loading.
      return true;
    }

    return playBuffer(tickBuffer, tickVolumeValue);
  };

  const playWebAudioCelebration = () => {
    const context = getAudioContext();
    if (!context) return false;

    resumeAudioContext(context);

    if (celebrationBuffer) {
      return playBuffer(celebrationBuffer, celebrationVolumeValue);
    }

    const promise = loadCelebrationBuffer();
    if (!promise) return false;

    void promise.then((buffer) => {
      if (buffer) {
        playBuffer(buffer, celebrationVolumeValue);
      }
    });

    return true;
  };

  const initTickAudioFallback = () => {
    if (typeof window === 'undefined' || getAudioContextConstructor()) return;

    if (!tickAudio) {
      tickAudio = new Audio(tickPath);
      tickAudio.volume = tickVolumeValue;
    }
  };

  const initCelebrationAudio = () => {
    if (typeof window === 'undefined') return;

    if (!celebrationAudio) {
      celebrationAudio = new Audio(celebrationPath);
      celebrationAudio.volume = celebrationVolumeValue;
    }
  };

  return {
    prepare() {
      const context = getAudioContext();
      if (!context) return;

      resumeAudioContext(context);
      void loadTickBuffer();
      void loadCelebrationBuffer();
    },

    playTick() {
      const now = Date.now();
      if (now - lastPlay < TICK_THROTTLE_MS) return;

      lastPlay = now;

      if (playWebAudioTick()) return;

      initTickAudioFallback();
      if (tickAudio) {
        tickAudio.currentTime = 0;
        tickAudio.play().catch(() => {
          // Ignore autoplay errors
        });
      }
    },

    playCelebration() {
      if (playWebAudioCelebration()) return;

      initCelebrationAudio();

      if (celebrationAudio) {
        celebrationAudio.currentTime = 0;
        celebrationAudio.play().catch(() => {
          // Ignore autoplay errors
        });
      }
    },

    stopAll() {
      if (tickAudio) {
        tickAudio.pause();
        tickAudio.currentTime = 0;
      }
      if (celebrationAudio) {
        celebrationAudio.pause();
        celebrationAudio.currentTime = 0;
      }
    },

    setVolume(tick?: number, celebration?: number) {
      if (tick !== undefined) {
        tickVolumeValue = Math.max(0, Math.min(1, tick));
      }
      if (celebration !== undefined) {
        celebrationVolumeValue = Math.max(0, Math.min(1, celebration));
      }
      if (tick !== undefined && tickAudio) {
        tickAudio.volume = tickVolumeValue;
      }
      if (celebration !== undefined && celebrationAudio) {
        celebrationAudio.volume = celebrationVolumeValue;
      }
    },

    dispose() {
      if (tickAudio) {
        tickAudio.pause();
        tickAudio = null;
      }
      if (celebrationAudio) {
        celebrationAudio.pause();
        celebrationAudio = null;
      }
      if (audioContext) {
        audioContext.close().catch(() => {
          // Ignore close errors during unmount.
        });
        audioContext = null;
      }
      tickBuffer = null;
      tickBufferPromise = null;
      celebrationBuffer = null;
      celebrationBufferPromise = null;
    },
  };
}

export type AudioManager = ReturnType<typeof createAudioManager>;
